import {
  getLogoUrl,
  logoMeta,
  type LogoKey,
  type LogoVariant
} from "./licenseLogos"
import { type NormalizedLicenseData } from "./normalizeLicenseData"

export type LicenseLogo = {
  key: LogoKey
  title: string
  src: string
  variant: LogoVariant
}

type LogoMapper = (data: NormalizedLicenseData) => LogoKey[]

const uniq = <T>(items: T[]): T[] => Array.from(new Set(items))

const mapAccess: LogoMapper = data =>
  (data.accessMode ?? []).reduce<LogoKey[]>((all, key) => {
    const lookup: Record<string, LogoKey> = {
      accessDirect: "sharing-direct",
      accessCollaborative: "sharing-collaborative",
      accessFederated: "sharing-federated",
      accessTRE: "sharing-tre"
    }
    const match = lookup[key]
    if (match) all.push(match)
    return all
  }, [])

const mapCommercial: LogoMapper = data => [
  data.commercialUse === "no" ? "commercial-no" : "commercial-yes"
]

const mapOnwardSharing: LogoMapper = data => [
  data.onwardSharing === "no" ? "sharing-no-onward" : "sharing-onward"
]

const mapShareAlike: LogoMapper = data =>
  data.onwardSharing === "yes" && data.shareAlike === "yes" ? ["share-alike"] : []

const mapModifications: LogoMapper = data =>
  data.onwardSharing === "yes" && data.modifications === "no" ? ["modifications-no"] : []

const mapBenefit: LogoMapper = data =>
  data.benefitSharing === "yes" ? ["benefit-sharing"] : []

const mapCostRecovery: LogoMapper = data =>
  data.costRecovery === "yes" ? ["cost-recovery"] : []

const mapPublishing: LogoMapper = data => {
  const base =
    data.publishingPermitted === "no" ? "publication-no" : "publication-yes"

  const extras: Record<string, LogoKey> = {
    embargo: "publication-embargo",
    review: "publication-review",
    reporting: "aggregated-data-reporting"
  }

  const additional =
    data.publishingPermitted === "yes"
      ? Object.entries(data.publishingRequirements)
          .filter(([, value]) => Boolean(value))
          .map(([key]) => extras[key])
          .filter(Boolean) as LogoKey[]
      : []

  return [base, ...additional]
}

const mapAttribution: LogoMapper = data => {
  if (data.attributionRequired === "no") return ["attribution-no"]

  const logos: LogoKey[] = ["attribution-yes"]

  if (data.attributionRequirements?.attribution ?? true) {
    logos.push("attribution-outputs")
  }

  if (data.attributionRequirements?.authorship) {
    logos.push("attribution-author")
  }

  return logos
}

const mappers: LogoMapper[] = [
  mapAccess,
  mapCommercial,
  mapOnwardSharing,
  mapShareAlike,
  mapModifications,
  mapBenefit,
  mapCostRecovery,
  mapPublishing,
  mapAttribution
]

export const getLicenseLogos = (
  normalizedData: NormalizedLicenseData,
  variant?: LogoVariant
): LicenseLogo[] => {
  const chosenVariant = variant ?? normalizedData.logoVariant ?? "color"
  const keys = uniq(mappers.flatMap(mapper => mapper(normalizedData)))

  return keys.map(key => ({
    key,
    title: logoMeta[key].title,
    src: getLogoUrl(key, chosenVariant),
    variant: chosenVariant
  }))
}
